import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiError, FieldValueTypeMap } from '@/shared/api';
import { AxiosError } from 'axios';

import { serializeObjectDeep } from '@/shared/api';
import { ErrorCodes, Errors } from '@/shared/constants';
import { endpointRequestMap } from './endpointMaps/';
import { defaultRequestKeysMap, defaultResponseKeysMap } from './http.constants';

export function onFulfilled(response: AxiosResponse) {
  const { success, errorMessage, ...payload } = response.data;

  if (Object.keys(payload).length === 1) {
    return Object.values(payload)[0];
  }

  return payload;
}

interface APIV2Response {
  success: boolean;
  errorMessage: string;
  errorCode: string;
  [key: string]: unknown;
}

export function onRejected(error: AxiosError<APIV2Response>) {
  if (error instanceof AxiosError && error.code === ErrorCodes.CANCELED) {
    return Promise.reject({
      errorMessage: error.message,
      errorCode: error.code,
    });
  }

  const errorData = error.response?.data;
  if (errorData) {
    const { success, errorMessage, errorCode, ...payload } = errorData;

    return Promise.reject({
      errorMessage,
      errorCode: errorCode ?? Errors.NO_NAME_ERROR,
      data: payload,
    });
  }

  return Promise.reject(error);
}

const stringToBooleanSerializer = (value: string) => Boolean(Number(value));
const yesNoToBooleanSerializer = (value: string) => value === 'Y';

const responseValuesTypesMap: FieldValueTypeMap = {
  totalPayment: Number,
  currentPrice: Number,
  paidPrice: Number,
  price: Number,
  pendingPrice: Number,
  installmentNumber: Number,
  modelYear: Number,
  isDowngraded: Boolean,
  isInstallment: stringToBooleanSerializer,
  isInstallmentPaid: stringToBooleanSerializer,
  hasAutopayment: yesNoToBooleanSerializer,
  isAutoPaymentFailed: yesNoToBooleanSerializer,
};

const requestValuesTypeMap: FieldValueTypeMap = {
  full: Number,
  selectproduct: Number,
};

export function responseSerializerInterceptor(response: AxiosResponse['data']) {
  return serializeObjectDeep(response, defaultResponseKeysMap, responseValuesTypesMap) as AxiosResponse;
}

export function requestSerializerInterceptor(request: AxiosRequestConfig) {
  const endpoint = request.url as keyof typeof endpointRequestMap;
  const responseKeysMap = endpointRequestMap[endpoint]
    ? endpointRequestMap[endpoint]
    : defaultRequestKeysMap;

  if (request.params) {
    return {
      ...request,
      params: serializeObjectDeep(request.params, responseKeysMap, requestValuesTypeMap),
    };
  }

  return request;
}

export function onFulfilledV3(response: AxiosResponse) {
  return response.data;
}

export function onRejectedV3(error: AxiosError<ApiError>) {
  if (error instanceof AxiosError && error.code === ErrorCodes.CANCELED) {
    return Promise.reject({
      errorMessage: error.message,
      errorCode: error.code,
    });
  }

  return Promise.reject(error?.response?.data ?? error);
}
